import { Injectable, inject } from '@angular/core';
import { GraphQlHistorical, GraphQlHistoricalSchema, IIqRateLaneUtilsStateService } from '../../models';
import {
    IDatiqGeoSuggestion,
    IqProxyGeoService,
} from '@dat/iq-proxy';
import { firstValueFrom } from 'rxjs';
import { DatiqGraphqlApolloService } from '@dat/datiq-graphql';
import { gql } from 'apollo-angular';
import { isEmpty } from 'lodash';
import { getHistoricalRatesDataQuery } from './query-fragments';

@Injectable({
    providedIn: 'root',
})
export class IqRateLaneUtilsStateService
    implements IIqRateLaneUtilsStateService
{
    _geoService: IqProxyGeoService = inject(IqProxyGeoService);

    cache: { [key: string]: any } = {};
    private readonly _apollo = inject(DatiqGraphqlApolloService);
    private readonly _geo = inject(IqProxyGeoService);

    async getGeo(location: string): Promise<IDatiqGeoSuggestion> {
        const res = await this._geoService.fetchFirstResult(location);
        return res;
    }

    async getFuelSurcharge(): Promise<{
        VAN: number;
        REEFER: number;
        FLATBED: number;
    }> {
        if (this.cache.fuelSurcharge) {
            return Promise.resolve(this.cache.fuelSurcharge);
        }

        const response = await firstValueFrom(
            this._apollo.getApollo().query({
                query: gql`
                    query {
                        FuelSurcharge {
                            VAN
                            REEFER
                            FLATBED
                        }
                    }
                `,
            }),
        );
        const fuelSurcharge = (response as any)?.data?.FuelSurcharge;
        if (fuelSurcharge) {
            this.cache.fuelSurcharge = fuelSurcharge;
            return fuelSurcharge;
        }

        return { VAN: 0, REEFER: 0, FLATBED: 0 };
    }

    async getMileage(origin: string, destination: string): Promise<number> {
        if (!origin?.length || !destination?.length) {
            return null;
        }

        const originSuggestion: IDatiqGeoSuggestion =
            await this._geo.fetchFirstResult(origin);
        const destinationSuggestion: IDatiqGeoSuggestion =
            await this._geo.fetchFirstResult(destination);

        if (!originSuggestion?.city || !destinationSuggestion?.city) {
            return null;
        }

        const historicalData = await firstValueFrom(
            this._apollo.getApollo().query({
                query: gql`
                    query (
                        $historicalQuery: HISTORICAL_SearchCriteriaInputHistorical
                    ) {
                        historical: getHistorical(query: $historicalQuery) {
                            mileage
                        }
                    }
                `,
                variables: {
                    historicalQuery: {
                        timeEscalation: 'SEVENDAYS',
                        equipmentCategory: 'VAN',
                        timeInterval: 'Monthly',
                        origin: {
                            city: originSuggestion.city,
                            stateProv: originSuggestion.stateProv,
                        },
                        destination: {
                            city: destinationSuggestion.city,
                            stateProv: destinationSuggestion.stateProv,
                        },
                        rateType: 'CONTRACT',
                    },
                },
            }),
        );
        let mileage = (historicalData as any)?.data?.historical?.mileage;
        return mileage || 0;
    }

    async getRates(
        equipmentType: string,
        rateType: string,
        origin: string,
        destination: string
    ): Promise<number> {
        if (!origin?.length || !destination?.length) {
            return 0;
        }

        const originSuggestion =
            await this._geoService.fetchFirstResult(origin);
        const destinationSuggestion =
            await this._geoService.fetchFirstResult(destination);

        const graphQlResponse = await this.makeHistoricalQuery(
            equipmentType,
            rateType,
            originSuggestion,
            destinationSuggestion,
        );

        const data = graphQlResponse?.data as GraphQlHistorical;
        const parseResult = GraphQlHistoricalSchema.safeParse(data);
        if (isEmpty(data) || !parseResult.success) {
            return 0;
        }

        /*
         * Note: shorthaul lanes will not have perMile values,
         * so we return 0 in those cases
         */
        const ratePerMile = data?.rate?.average?.perMile?.value;
        return !!ratePerMile ? ratePerMile : 0;
    }

    private makeHistoricalQuery(
        equipmentType: string,
        rateType: string,
        originSuggestion: IDatiqGeoSuggestion,
        destinationSuggestion: IDatiqGeoSuggestion
    ) {
        if (
            !this.isSuggestionValid(originSuggestion)
            || !this.isSuggestionValid(destinationSuggestion)
        ) {
            throw new Error('no geo suggestion');
        }

        return firstValueFrom(
            this._apollo.getApollo().query({
                query: gql`
                    ${getHistoricalRatesDataQuery}
                `,
                variables: {
                    historicalQuery: {
                        equipmentCategory: equipmentType,
                        timeEscalation: 'THREEDAYS',
                        timeInterval: 'CurrentDay',
                        origin: {
                            city: originSuggestion.city,
                            stateProv: originSuggestion.stateProv,
                            postalCode: originSuggestion.postalCode,
                        },
                        destination: {
                            city: destinationSuggestion.city,
                            stateProv: destinationSuggestion.stateProv,
                            postalCode: destinationSuggestion.postalCode,
                        },
                        rateType: rateType,
                    },
                },
            }),
        );
    }

    private isSuggestionValid(suggestion: IDatiqGeoSuggestion): boolean {
        return (
            !!suggestion.postalCode?.length &&
            !!suggestion.stateProv?.length &&
            !!suggestion.city?.length
        );
    }
}
